import type { Document, DocumentVaultType } from '@/api/generated/model';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { ShimmerText } from '@/components/ui/shimmer-text';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { Link2, Upload, X } from 'lucide-react';
import { Link } from 'react-router-dom';

type SuccessionDocSlotKey = 'will' | 'powerOfAttorney' | 'trustDeed' | 'tax';

interface SuccessionDocSlot {
  key: SuccessionDocSlotKey;
  documentType: DocumentVaultType | null;
}

const SUCCESSION_DOC_SLOTS: SuccessionDocSlot[] = [
  { key: 'will', documentType: 'will' },
  { key: 'powerOfAttorney', documentType: null },
  { key: 'trustDeed', documentType: 'trust_deed' },
  { key: 'tax', documentType: 'tax' },
];

const SHIMMER_DOC_NAMES: Record<SuccessionDocSlotKey, string> = {
  will: 'Will.pdf',
  powerOfAttorney: 'POA.pdf',
  trustDeed: 'Trust deed.pdf',
  tax: 'Estate plan.pdf',
};

function documentLabel(document: Document): string {
  return document.organization ? `${document.name} (${document.organization})` : document.name;
}

function linkedDocumentForSlot(
  slot: SuccessionDocSlot,
  documents: Document[],
  linkedDocumentIds: string[]
): Document | undefined {
  const linked = documents.filter((document) => linkedDocumentIds.includes(document.id));
  if (slot.documentType) {
    return linked.find((document) => document.documentType === slot.documentType);
  }
  return linked.find(
    (document) =>
      !SUCCESSION_DOC_SLOTS.some(
        (candidate) => candidate.documentType && document.documentType === candidate.documentType
      )
  );
}

function availableDocumentsForSlot(
  slot: SuccessionDocSlot,
  documents: Document[],
  linkedDocumentIds: string[]
): Document[] {
  return documents.filter((document) => {
    if (linkedDocumentIds.includes(document.id)) return false;
    if (slot.documentType) {
      return document.documentType === slot.documentType;
    }
    return !SUCCESSION_DOC_SLOTS.some(
      (candidate) => candidate.documentType && document.documentType === candidate.documentType
    );
  });
}

interface SuccessionDocumentSlotsProps {
  documents: Document[];
  linkedDocumentIds: string[];
  onLink: (documentId: string) => void;
  onUnlink: (documentId: string) => void;
  isLoading?: boolean;
}

export function SuccessionDocumentSlots({
  documents,
  linkedDocumentIds,
  onLink,
  onUnlink,
  isLoading = false,
}: SuccessionDocumentSlotsProps) {
  const { t } = useLanguage();
  const linkLabel = t('planning.succession.documentSlots.linkFromVault');
  const uploadLabel = t('planning.succession.documentSlots.upload');

  return (
    <div className='space-y-3'>
      <Label>{t('planning.succession.documentsTitle')}</Label>
      <div className='border-fin-gray-800 divide-fin-gray-800 divide-y rounded-lg border'>
        {SUCCESSION_DOC_SLOTS.map((slot) => {
          const linked = linkedDocumentForSlot(slot, documents, linkedDocumentIds);
          const available = availableDocumentsForSlot(slot, documents, linkedDocumentIds);
          const label = t(`planning.succession.documentSlots.${slot.key}`);

          return (
            <div key={slot.key} className='flex min-w-0 items-center gap-2 px-4 py-3'>
              <span className='min-w-0 flex-1 truncate text-sm font-medium'>{label}</span>
              {isLoading ? (
                <div className='flex shrink-0 items-center gap-1 opacity-80'>
                  <span className='text-fin-accent max-w-[9rem] truncate text-sm sm:max-w-xs'>
                    <ShimmerText>{SHIMMER_DOC_NAMES[slot.key]}</ShimmerText>
                  </span>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='size-8 shrink-0'
                    disabled
                    aria-label={linkLabel}
                  >
                    <Link2 className='size-4' aria-hidden />
                  </Button>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='size-8 shrink-0'
                    disabled
                    aria-label={uploadLabel}
                  >
                    <Upload className='size-4' aria-hidden />
                  </Button>
                </div>
              ) : linked ? (
                <div className='flex min-w-0 shrink-0 items-center gap-1'>
                  <Link
                    to={`/vault/${linked.id}`}
                    className='text-fin-accent max-w-[9rem] truncate text-sm hover:underline sm:max-w-xs'
                    title={documentLabel(linked)}
                  >
                    {documentLabel(linked)}
                  </Link>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='size-8 shrink-0'
                    aria-label={t('common.remove')}
                    onClick={() => onUnlink(linked.id)}
                  >
                    <X className='size-4' aria-hidden />
                  </Button>
                </div>
              ) : (
                <div className={cn('flex shrink-0 items-center gap-1', isLoading && 'opacity-80')}>
                  {available.length === 0 ? (
                    <span className='text-muted-foreground hidden max-w-[6rem] truncate text-xs sm:inline'>
                      {t('planning.succession.documentSlots.notLinked')}
                    </span>
                  ) : null}
                  {available.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          className='size-8 shrink-0'
                          aria-label={linkLabel}
                        >
                          <Link2 className='size-4' aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align='end'
                        className='max-w-[min(20rem,calc(100vw-2rem))]'
                      >
                        {available.map((document) => (
                          <DropdownMenuItem
                            key={document.id}
                            className='truncate'
                            onSelect={() => onLink(document.id)}
                          >
                            {documentLabel(document)}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      className='size-8 shrink-0'
                      disabled
                      aria-label={linkLabel}
                    >
                      <Link2 className='size-4' aria-hidden />
                    </Button>
                  )}
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='size-8 shrink-0'
                    asChild
                  >
                    <Link to='/vault' aria-label={uploadLabel}>
                      <Upload className='size-4' aria-hidden />
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
